import React, {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {
  selectIsCityCaptureAnimationPlaying,
  selectIsTutorialActive,
  selectIsTutorialChapterCompleted,
  selectIsTutorialRepeatRun,
  selectShowConstructionPhaseInfoModal,
  selectTutorialActiveModalId,
  selectTutorialChapterId,
  selectTutorialStepIndex
} from '../../redux/reducers/privateStateReducer/selectors';
import {
  selectIsCityCaptureModalVisible,
  selectIsCombatResultsModalVisible,
  selectIsRoundEndModalVisible
} from '../../redux/selectors/modalVisibility';
import {TUTORIAL_MODALS_REGISTRY} from './tutorialModalsRegistry';
import TutorialChapterCompleteModal from './TutorialChapterCompleteModal/TutorialChapterCompleteModal';
import {sendTutorialEvent} from '../../analytics/gameAnalytics';
import {
  getTutorialModalKey,
  getTutorialRunKind,
  getTutorialStepKey,
  TUTORIAL_ANALYTICS_EVENTS
} from '../../utils/tutorial/tutorialAnalyticsIds';
import {getTutorialChapter} from '../../configs/tutorial';

/**
 * Хост обучающих модалок: рендерит компонент по activeModalId из реестра, а
 * после прохождения главы — поздравление с навигацией. Монтируется в
 * GameContent только в режиме обучения.
 *
 * Пока открыта какая-то из общеигровых модалок (итоги боя, захват города,
 * итоги раунда) — обучающая модалка не рендерится: activeModalId остается в
 * сторе, и она появится сразу после закрытия игровой (видимость берется из тех
 * же селекторов, что и у самих этих модалок). Так же она ждет облета
 * захваченных городов на поле (см. scenes/CityCaptureAnimation)
 */
const useIsGameModalOpen = () => {
  const isCombatResultsOpen = useSelector(selectIsCombatResultsModalVisible);
  const isCityCaptureOpen = useSelector(selectIsCityCaptureModalVisible);
  const isCityCaptureAnimationPlaying = useSelector(selectIsCityCaptureAnimationPlaying);
  const isRoundEndOpen = useSelector(selectIsRoundEndModalVisible);
  // Пока игрок читает правила, обучающие модалки тоже ждут
  const showRulesModal = useSelector(selectShowConstructionPhaseInfoModal);

  return isCombatResultsOpen || isCityCaptureOpen || isCityCaptureAnimationPlaying || isRoundEndOpen
    || Boolean(showRulesModal);
};

/**
 * Метрики шагов главы (см. tutorialAnalyticsIds): событие на каждый шаг,
 * до которого дошел игрок. Одним экшеном сценарий может пройти несколько
 * шагов подряд — отправляются все, ни один номер воронки не пропадает.
 * Хост живет ровно одно прохождение главы (GameContent перемонтируется на
 * каждую главу), поэтому помнить отправленное достаточно в ref
 */
const useTutorialStepAnalytics = () => {
  const chapterId = useSelector(selectTutorialChapterId);
  const stepIndex = useSelector(selectTutorialStepIndex);
  const isRepeatRun = useSelector(selectIsTutorialRepeatRun);
  const reportedRef = useRef({chapterId: null, lastStepIndex: -1});

  useEffect(() => {
    if (!chapterId) {
      return;
    }
    if (reportedRef.current.chapterId !== chapterId) {
      reportedRef.current = {chapterId, lastStepIndex: -1};
    }
    const stepsCount = getTutorialChapter(chapterId)?.steps?.length ?? 0;
    const lastReachedIndex = Math.min(stepIndex, stepsCount - 1);
    for (let index = reportedRef.current.lastStepIndex + 1; index <= lastReachedIndex; index++) {
      const stepKey = getTutorialStepKey(chapterId, index);
      if (stepKey) {
        sendTutorialEvent(chapterId, TUTORIAL_ANALYTICS_EVENTS.STEP, stepKey, getTutorialRunKind(isRepeatRun));
      }
    }
    reportedRef.current.lastStepIndex = Math.max(reportedRef.current.lastStepIndex, lastReachedIndex);
  }, [chapterId, stepIndex, isRepeatRun]);
};

const TutorialModalHost = ({onNextChapter, onOpenInventory, onExitToMenu, onBattle}) => {
  const isTutorialActive = useSelector(selectIsTutorialActive);
  const chapterId = useSelector(selectTutorialChapterId);
  const isRepeatRun = useSelector(selectIsTutorialRepeatRun);
  const activeModalId = useSelector(selectTutorialActiveModalId);
  const isChapterCompleted = useSelector(selectIsTutorialChapterCompleted);
  const isGameModalOpen = useIsGameModalOpen();

  useTutorialStepAnalytics();

  const ModalComponent = activeModalId ? TUTORIAL_MODALS_REGISTRY[activeModalId] : null;
  // Модалка, которую игрок видит прямо сейчас: activeModalId в сторе еще не
  // значит показ — за игровой модалкой обучающая ждет
  const renderedModalId = isTutorialActive && !isChapterCompleted && !isGameModalOpen && ModalComponent
    ? activeModalId
    : null;

  // Показ модалки — один раз за прохождение: пока игровая модалка ее
  // перекрывает, компонент размонтируется и потом монтируется заново
  const shownModalIdsRef = useRef(new Set());
  useEffect(() => {
    if (!renderedModalId || !chapterId || shownModalIdsRef.current.has(renderedModalId)) {
      return;
    }
    shownModalIdsRef.current.add(renderedModalId);
    const modalKey = getTutorialModalKey(chapterId, renderedModalId);
    if (modalKey) {
      sendTutorialEvent(chapterId, TUTORIAL_ANALYTICS_EVENTS.MODAL_SHOWN, modalKey, getTutorialRunKind(isRepeatRun));
    }
  }, [renderedModalId, chapterId, isRepeatRun]);

  if (!isTutorialActive) {
    return null;
  }

  // Поздравление (и наградная модалка внутри него) тоже ждет закрытия игровых
  // модалок — захват города / итоги раунда показываются первыми. Компонент
  // при этом смонтирован сразу: сохранение прохождения и начисление награды
  // уходят на сервер в момент завершения главы, а не после кликов игрока
  if (isChapterCompleted) {
    return (
      <TutorialChapterCompleteModal
        isWaitingForGameModals={isGameModalOpen}
        onNextChapter={onNextChapter}
        onOpenInventory={onOpenInventory}
        onExitToMenu={onExitToMenu}
        onBattle={onBattle}
      />
    );
  }

  if (!activeModalId || isGameModalOpen) {
    return null;
  }

  if (!ModalComponent) {
    console.error(`[Tutorial] Modal is not registered: ${activeModalId}`);
    return null;
  }

  return <ModalComponent/>;
};

export default TutorialModalHost;
